import { useState } from 'react';
import { Icon, Spinner } from './ui/Icon.js';

type ProcessingState = 'idle' | 'running' | 'complete' | 'stale' | 'never';

interface ProcessingStatusProps {
  label: string;
  lastRun: string | null;
  count?: number | null;
  countLabel?: string;
  status: ProcessingState;
  onRun?: () => Promise<void>;
  runLabel?: string;
  compact?: boolean;
}

function formatRelativeTime(dateStr: string): string {
  const date = new Date(dateStr);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMin = Math.floor(diffMs / 60000);
  const diffHr = Math.floor(diffMs / 3600000);
  const diffDay = Math.floor(diffMs / 86400000);
  if (diffMin < 1) return 'just now';
  if (diffMin < 60) return `${diffMin}m ago`;
  if (diffHr < 24) return `${diffHr}h ago`;
  if (diffDay < 7) return `${diffDay}d ago`;
  return date.toLocaleDateString();
}

/** `icon` is a Boxicon name, not a component (Spec 230); `running` renders
 * through `Spinner` instead, since `bx-loader-alt` alone isn't animated. */
const STATUS_CONFIG: Record<ProcessingState, { icon: string; color: string; iconColor: string }> = {
  complete: { icon: 'bx-check-circle', color: 'text-green-700', iconColor: 'text-green-500' },
  stale: { icon: 'bx-error', color: 'text-amber-700', iconColor: 'text-amber-500' },
  running: { icon: 'bx-loader-alt', color: 'text-violet-700', iconColor: 'text-violet-500' },
  idle: { icon: 'bx-circle', color: 'text-slate-500', iconColor: 'text-slate-400' },
  never: { icon: 'bx-circle', color: 'text-slate-400', iconColor: 'text-slate-300' },
};

export function ProcessingStatus({
  label,
  lastRun,
  count,
  countLabel,
  status,
  onRun,
  runLabel = 'Run',
  compact = false,
}: ProcessingStatusProps) {
  const [isRunning, setIsRunning] = useState(false);
  const effectiveStatus = isRunning ? 'running' : status;
  const config = STATUS_CONFIG[effectiveStatus];

  const handleRun = async () => {
    if (!onRun || isRunning) return;
    setIsRunning(true);
    try {
      await onRun();
    } finally {
      setIsRunning(false);
    }
  };

  const timeStr = lastRun ? formatRelativeTime(lastRun) : 'never';
  const countStr =
    count != null && countLabel
      ? `${count} ${count === 1 && countLabel.endsWith('s') ? countLabel.slice(0, -1) : countLabel}`
      : null;

  if (compact) {
    return (
      <div
        className={`flex items-center gap-2 rounded-md border px-3 py-2 text-xs ${
          effectiveStatus === 'stale'
            ? 'border-amber-200 bg-amber-50/50'
            : effectiveStatus === 'complete'
              ? 'border-green-200 bg-green-50/30'
              : 'border-slate-200 bg-white'
        }`}
        data-testid={`processing-status-${label.toLowerCase().replace(/\s+/g, '-')}`}
      >
        <Icon
          name={config.icon}
          className={`flex-shrink-0 text-sm ${config.iconColor} ${effectiveStatus === 'running' ? 'animate-spin' : ''}`}
        />
        <span className="font-medium text-slate-700">{label}</span>
        {countStr && <span className="text-slate-500">{countStr}</span>}
        <span className={`${config.color}`}>{timeStr}</span>
        {onRun && (
          <button
            onClick={handleRun}
            disabled={isRunning}
            className="hub-btn hub-btn--ghost ml-auto"
          >
            {isRunning && <Spinner className="text-xs" />}
            {runLabel}
          </button>
        )}
      </div>
    );
  }

  // Card mode
  return (
    <div
      className={`rounded-lg border p-4 ${
        effectiveStatus === 'stale'
          ? 'border-amber-200 bg-amber-50/30'
          : effectiveStatus === 'complete'
            ? 'border-green-100 bg-white'
            : 'border-slate-200 bg-white'
      }`}
      data-testid={`processing-status-${label.toLowerCase().replace(/\s+/g, '-')}`}
    >
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Icon
            name={config.icon}
            className={`text-base ${config.iconColor} ${effectiveStatus === 'running' ? 'animate-spin' : ''}`}
          />
          <span className="text-sm font-medium text-slate-800">{label}</span>
        </div>
        {onRun && (
          <button
            onClick={handleRun}
            disabled={isRunning}
            className="hub-btn hub-btn--ghost"
          >
            {isRunning && <Spinner className="text-xs" />}
            {runLabel}
          </button>
        )}
      </div>
      <div className="mt-2 flex items-center gap-3 text-xs">
        {countStr && <span className="text-slate-600">{countStr}</span>}
        <span className={config.color}>Last run: {timeStr}</span>
        {effectiveStatus === 'stale' && (
          <span className="rounded-md bg-amber-100 px-1.5 py-0.5 text-amber-700">Stale</span>
        )}
      </div>
    </div>
  );
}
